/**
 * Lenis smooth scroll: instance, window.lenis, raf loop,
 * and GSAP ScrollTrigger proxy so scroll-driven animations use Lenis scroll position.
 */
import type { LenisInstance } from '../../types';

export interface LenisControllerOptions {
  lerp?: number;
  duration?: number;
}

export class LenisController {
  private readonly options: Required<LenisControllerOptions>;
  private instance: LenisInstance | null = null;
  private rafId: number | null = null;

  constructor(options: LenisControllerOptions = {}) {
    this.options = {
      lerp: options.lerp ?? 0.08,
      duration: options.duration ?? 1.2,
    };
  }

  /**
   * Create Lenis, attach to window, register ScrollTrigger proxy, start raf loop.
   */
  init(): LenisInstance | null {
    if (typeof window === 'undefined' || typeof window.Lenis === 'undefined') return null;
    const gsap = window.gsap;
    if (!gsap) return null;

    const lenis = new window.Lenis({
      lerp: this.options.lerp,
      duration: this.options.duration,
    });

    this.instance = lenis;
    window.lenis = lenis;

    const scroller = document.documentElement;
    const ST = window.ScrollTrigger;

    if (typeof ST !== 'undefined') {
      gsap.registerPlugin(ST);
      ST.scrollerProxy(scroller, {
        scrollTop: (v?: number) => {
          if (v !== undefined) lenis.scrollTo(v, { immediate: true });
          return lenis.scroll;
        },
        getBoundingClientRect: () =>
          new DOMRect(0, 0, window.innerWidth, window.innerHeight),
      });
      lenis.on('scroll', ST.update);
    }

    const raf = (time: number): void => {
      lenis.raf(time);
      this.rafId = requestAnimationFrame(raf);
    };
    this.rafId = requestAnimationFrame(raf);

    return lenis;
  }

  getInstance(): LenisInstance | null {
    return this.instance;
  }

  destroy(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.instance) {
      this.instance.stop();
      if (typeof window !== 'undefined' && window.lenis === this.instance) {
        window.lenis = undefined;
      }
      this.instance = null;
    }
  }
}

const TOP_BTN_SELECTOR = '.top_btn';

/**
 * 현재 스크롤 위치 (Lenis 사용 시 lenis.scroll, 없으면 window.scrollY).
 * HeaderEffects 등에서 공통 사용.
 */
export function getScrollTop(): number {
  if (typeof window === 'undefined') return 0;
  const lenis = window.lenis;
  if (lenis != null && typeof lenis.scroll === 'number') return lenis.scroll;
  return window.scrollY ?? document.documentElement.scrollTop ?? 0;
}

/**
 * Bind "Back to Top" buttons. Uses Lenis scrollTo(0) when Lenis is present, else window.scrollTo.
 */
export function initBackToTop(): void {
  if (typeof document === 'undefined') return;

  document.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest(TOP_BTN_SELECTOR);
    if (!btn) return;

    e.preventDefault();

    const lenis = window.lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(0, { immediate: false });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
}
